import { findPunctualityReportsByCriteria } from '@src/main/modules/reports/punctuality/application/use-cases/find-punctuality-reports-by-criteria.usecase';
import { createPunctualityReportHttpRepository } from '@src/main/modules/reports/punctuality/infrastructure/repository/PunctualityReportHttpRepository';
import {
  PunctualityReport,
  PunctualityReportCriteria,
} from '@src/main/modules/reports/shared/domain/report.interface';
import { PunctualityFilterMeta } from '@src/main/modules/reports/ui/mapPunctualityFiltersToCriteria';
import { useAuth } from '@src/main/shared/context/AuthContext';
import { PageResponse } from '@src/main/shared/infrastructure/entity/page.entity';
import { useQuery } from '@tanstack/react-query';
import { DataTablePageEvent, DataTableSortEvent } from 'primereact/datatable';
import { useCallback, useMemo, useState } from 'react';

export function usePunctualityReports(
  initialFilters: PunctualityFilterMeta,
  mapFiltersToCriteria: (
    filters: PunctualityFilterMeta,
    emptyCriteria: PunctualityReportCriteria,
  ) => PunctualityReportCriteria,
  emptyCriteria: PunctualityReportCriteria,
) {
  const { user } = useAuth();
  const [filters, setFilters] = useState<PunctualityFilterMeta>(initialFilters);
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string>('flightDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const getToken = useCallback(async () => user?.token ?? '', [user?.token]);
  const tokenProvider = useMemo(() => ({ getToken }), [getToken]);

  const repository = useMemo(
    () => (user?.token ? createPunctualityReportHttpRepository(tokenProvider) : null),
    [tokenProvider, user?.token],
  );

  const resetPage = useCallback(() => setPageNumber(0), []);

  const criteria = useMemo(() => {
    const base = mapFiltersToCriteria(filters, emptyCriteria);
    return {
      ...base,
      offset: pageNumber * pageSize,
      limit: pageSize,
      sortField,
      sortOrder,
    };
  }, [filters, emptyCriteria, pageNumber, pageSize, sortField, sortOrder, mapFiltersToCriteria]);

  const { data, isLoading, refetch, isFetching } = useQuery<PageResponse<PunctualityReport>>({
    queryKey: ['punctualityReports', criteria],
    queryFn: async () => {
      if (!repository) throw new Error('Repository not available');
      return await findPunctualityReportsByCriteria(repository, criteria);
    },
    enabled: !!repository,
    placeholderData: (prev) => prev,
    staleTime: 1000 * 60,
  });

  const handlePageChange = useCallback((e: DataTablePageEvent) => {
    setPageNumber(Math.floor(e.first / e.rows));
    setPageSize(e.rows);
  }, []);

  const handleSort = useCallback((event: DataTableSortEvent) => {
    setSortField(event.sortField ?? 'flightDate');
    setSortOrder(event.sortOrder === 1 ? 'asc' : 'desc');
  }, []);

  return {
    filters,
    setFilters,
    reports: data?.items ?? [],
    total: data?.total ?? 0,
    isLoading,
    isFetching,
    pageNumber,
    pageSize,
    handlePageChange,
    resetPage,
    refetchReports: refetch,
    handleSort,
    sortField,
    sortOrder,
  };
}
